import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Grid, Button, Container } from '@material-ui/core';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import { useConfirm } from 'hooks';
import Logs from 'components/Logs';
import Title from 'components/Title';
import Layout from 'components/Layout';
import Comments from 'components/Comments';
import { transformAsyncErrors } from 'utils/utils';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useParams } from 'react-router-dom';
import { DocumentsActions } from 'state/modules/documents';
import VersionModal from 'components/Documents/VersionModal';
import DocumentModal from 'components/Documents/DocumentModal';
import VersionsWidget from 'components/Documents/VersionsListWidget';
import VersionDetailsWidget from 'components/Documents/VersionDetailsWidget';
import DocumentDetailsWidget from 'components/Documents/DocumentDetailsWidget';

const useStyles = makeStyles((theme) => ({
  actionBtn: {
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(2),
    marginTop: theme.spacing(1),
    textTransform: 'none',
  },
  container: {
    minWidth: '100%',
    margin: 0,
  },
}));

const DocumentsDetailsPage = () => {
  const classes = useStyles();
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const [asyncErrors, setAsyncErrors] = useState({});
  const [renameModal, setRenamModal] = useState(false);
  const [createVersionModal, setCreateVersionModal] = useState(false);
  const document = useSelector((state) => state.documents.byId[id] || {});
  const isUploading = useSelector((state) => state.documents.isUploading);

  const [toggleDeleteDocument, DeleteDocumentConfirm] = useConfirm({
    title: i18n.t('documents.delete_document'),
    message: i18n.t('documents.delete_document_msg'),
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    onConfirm: useCallback(
      () =>
        dispatch(DocumentsActions.deleteDocument(id, () => history.push(buildUrl('/documents')))),
      [dispatch, history, id]
    ),
  });

  const toggleVersionModal = () => {
    if (createVersionModal) {
      setAsyncErrors({});
    }
    setCreateVersionModal(!createVersionModal);
  };

  const toggleRenameModal = () => {
    setRenamModal(!renameModal);
  };

  const onCreate = async (data) => {
    const response = await dispatch(DocumentsActions.createVersion(id, data));
    if (response.ok) {
      toggleVersionModal();
      history.push(buildUrl(`/documents/${id}/versions/${response.data.id}`));
    } else {
      setAsyncErrors({
        ...asyncErrors,
        createVersion: transformAsyncErrors(response?.data?.details),
      });
    }
  };

  /**
   * Rename document
   * @param {string} name
   */
  const onRename = async (document_type_id, description) => {
    const payload = { document_type_id, description };
    Object.keys(payload).forEach((key) => {
      if (!payload[key]) payload[key] = null;
    });
    await dispatch(DocumentsActions.updateDocument(id, payload));
    toggleRenameModal();
  };

  useEffect(() => {
    dispatch(DocumentsActions.fetchDocument(id));
    dispatch(DocumentsActions.resetCurrentVersion());
  }, [id, dispatch]);

  /**
   * Navigate to version details screen
   * @param {string} versionId Id of the version
   */
  const goToVersion = (versionId) => {
    history.push(buildUrl(`/documents/${id}/versions/${versionId ? versionId : ''}`));
  };

  /**
   * Handle tag click and navigate to link
   */
  const handleTagClick = () => {
    switch (document.source_type) {
      case 'User': {
        history.push(buildUrl(`/customers/${document.source_id}`));
        return;
      }
      case 'Vehicle': {
        history.push(buildUrl(`/vehicles/${document.source_id}`));
        return;
      }
      default:
        return;
    }
  };

  const onUpdateVersion = (data, versionId) => {
    dispatch(DocumentsActions.updateVersion(id, versionId, data, null, true));
  };

  const onUploadFile = (versionId, file) => {
    dispatch(DocumentsActions.uploadVersionsFile(id, versionId, file));
  };

  const onRemoveFile = (versionId) => {
    dispatch(DocumentsActions.deleteVersionsFile(id, versionId));
  };

  const menuItems = [
    {
      title: i18n.t('documents.create_new_version'),
      can: { name: 'document', action: 'create' },
      onClick: toggleVersionModal,
    },
  ];

  const getTags = () => {
    return document && document?.source_type && document?.source_type
      ? [
          {
            label: `${
              document.source_type === 'User' ? i18n.t('customers.customer') : document.source_type
            } #${document.source_id}`,
          },
        ]
      : [];
  };

  return (
    <Layout title={i18n.t('documents.documents_details')}>
      <Container className={classes.container}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Grid container justifyContent="space-between" style={{ marginBottom: 20 }}>
              <Grid>
                <Title
                  title={`${i18n.t('documents.documents_details')} ${
                    document?.document_type?.name ? '- ' + document?.document_type?.name : ''
                  }`}
                />
              </Grid>
              <Grid item>
                <Box display="flex" flexWrap="wrap">
                  <Button
                    color="primary"
                    variant="outlined"
                    data-cy="create-document"
                    className={classes.actionBtn}
                    onClick={toggleVersionModal}
                  >
                    {i18n.t('documents.create_new_version')}
                  </Button>
                  {document.default?.id && (
                    <Button
                      color="primary"
                      variant="outlined"
                      data-cy="rename-document"
                      onClick={toggleRenameModal}
                      className={classes.actionBtn}
                    >
                      {i18n.t('documents.edit_document')}
                    </Button>
                  )}
                  <Button
                    color="primary"
                    variant="contained"
                    data-cy="delete-document"
                    className={classes.actionBtn}
                    onClick={toggleDeleteDocument}
                  >
                    {i18n.t('documents.delete_document')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item sm={12} md={6} lg={4}>
            <>
              <DocumentDetailsWidget
                document={document}
                tags={getTags()}
                onTagClick={handleTagClick}
              />
              <Box mt={3}></Box>
              <VersionsWidget documentId={id} menuItems={menuItems} />
            </>
          </Grid>
          <Grid item sm={12} md={6} lg={4}>
            <>
              <VersionDetailsWidget
                document={parseInt(id)}
                id={document?.default?.id}
                handleChange={onUpdateVersion}
                handleUploadFile={onUploadFile}
                handleRemoveFile={onRemoveFile}
                onViewDetails={goToVersion}
                isUploading={isUploading}
              />
              <Box mt={3}></Box>
            </>
            <Comments id={parseInt(id)} type="document" />
          </Grid>
          <Grid item sm={12} md={6} lg={4}>
            <Logs
              targetId={parseInt(id)}
              targetType="Document"
              title="Document logs"
              height={600}
            />
          </Grid>
        </Grid>
      </Container>
      {createVersionModal && (
        <VersionModal
          mode={'create'}
          handleCreate={onCreate}
          open={createVersionModal}
          handleClose={toggleVersionModal}
          asyncErrors={asyncErrors.createVersion}
        />
      )}
      {renameModal && (
        <DocumentModal
          mode={'update'}
          open={renameModal}
          document_type_id={document?.document_type?.id}
          document_type_name={document?.document_type?.name}
          description={document.description}
          handleCreate={onRename}
          handleClose={toggleRenameModal}
        />
      )}
      <DeleteDocumentConfirm />
    </Layout>
  );
};

export default DocumentsDetailsPage;
